import { apiClient } from '@/libs/api_client'
import axios from 'axios'
import {
  MarqueeResponse,
  CreateMarqueeRequest,
  UpdateMarqueeRequest,
  MarqueeListResponse,
} from '@magnetar/admin-api-interfaces'
import { logger } from '@magnetar/utils'

export const marqueeService = {
  createMarquee: async (
    data: CreateMarqueeRequest & { broadcast_device_id: number | string },
    token: string,
  ): Promise<MarqueeResponse> => {
    try {
      const response = await apiClient.post<MarqueeResponse>(`/api/marquee`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as MarqueeResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'CREATE_MARQUEE_FAILED',
          message: '建立跑馬燈失敗',
        },
      }
    }
  },

  updateMarquee: async (
    marquee_id: number,
    data: UpdateMarqueeRequest,
    token: string,
  ): Promise<MarqueeResponse> => {
    try {
      const response = await apiClient.put<MarqueeResponse>(`/api/marquee/${marquee_id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as MarqueeResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'UPDATE_MARQUEE_FAILED',
          message: '更新跑馬燈失敗',
        },
      }
    }
  },

  deleteMarquee: async (marquee_id: number, token: string): Promise<MarqueeResponse> => {
    try {
      const response = await apiClient.delete<MarqueeResponse>(`/api/marquee/${marquee_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as MarqueeResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'DELETE_MARQUEE_FAILED',
          message: '刪除跑馬燈失敗',
        },
      }
    }
  },

  // 批次更新跑馬燈
  batchUpdateMarquees: async (
    broadcast_device_id: number | string,
    marquees: {
      marquee_id?: number
      marquee_content: string
      marquee_switching_time: number
      is_active: boolean
      order: number
    }[],
    token: string,
  ): Promise<MarqueeListResponse> => {
    try {
      // 驗證必要的參數
      if (!broadcast_device_id) {
        throw new Error('缺少廣播設備 ID')
      }

      if (!token) {
        throw new Error('缺少認證令牌')
      }

      // 驗證跑馬燈數據
      if (!Array.isArray(marquees)) {
        throw new Error('跑馬燈數據必須為數組')
      }

      // 驗證每個跑馬燈的必要欄位
      for (const marquee of marquees) {
        if (!marquee.marquee_content) {
          throw new Error('跑馬燈內容不能為空')
        }

        if (
          typeof marquee.marquee_switching_time !== 'number' ||
          marquee.marquee_switching_time <= 0
        ) {
          throw new Error('轉換時間必須大於 0')
        }

        if (typeof marquee.order !== 'number' || marquee.order < 0) {
          throw new Error('排序必須為非負數')
        }
      }

      logger.log('批次更新跑馬燈:', {
        broadcast_device_id,
        marquees_count: marquees.length,
      })

      const response = await apiClient.put<MarqueeListResponse>(
        `/api/broadcast-device/${broadcast_device_id}/marquees`,
        { marquees },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      logger.log('批次更新跑馬燈成功:', {
        success: response.data.success,
        marquees_count: response.data.data?.length ?? 0,
      })

      return response.data
    } catch (error) {
      logger.error('批次更新跑馬燈失敗:', error)

      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data as MarqueeListResponse
        }

        // 網路錯誤
        return {
          success: false,
          data: null,
          error: {
            code: 'NETWORK_ERROR',
            message: '網路連線錯誤',
          },
        }
      }

      // 其他錯誤
      return {
        success: false,
        data: null,
        error: {
          code: 'BATCH_UPDATE_MARQUEES_FAILED',
          message: error instanceof Error ? error.message : '批次更新跑馬燈失敗',
        },
      }
    }
  },
}
